/* eslint-disable max-lines */
import trim from 'lodash/trim'
import { AggregationPipeline } from 'types/common/aggregation'
import { AuthRoleIdEnum } from 'constants/user'
import { IGroupMemberPermissionId } from 'interfaces/group'
import { getGroupFilterPipeline } from './groupFilter'

/*
  INFO
    Process
    - Basic user : Can only see process in groups he belong to
    - Admin : Can see all
    Draft
    - Basic user & Admin : Only creator and collaborator can see
*/

export function getProcessPipeline(
  organizationId: number,
  limit: number,
  skip: number,
  collectionIds: number[],
  creatorIds: number[],
  documentTypeIds: number[],
  groupIds: number[],
  tagIds: number[],
  sortField: string,
  isCounting = false,
  isPublished: boolean | null = true,
  userId: number | undefined,
  userRoleId: AuthRoleIdEnum,
  isArchived?: boolean,
  keyword?: string,
  onlyGetProcessThatUserHaveEditPermission?: boolean
): AggregationPipeline {
  const filterCollectionPipeline: any = []
  const filterTagPipeline = [
    {
      $lookup: {
        from: 'ProcessesTag',
        localField: '_id',
        foreignField: 'processId',
        as: 'processesTag'
      }
    }
  ]
  const match: Record<string, unknown> = {
    organizationId,
    isPublished,
    isDeleted: {
      $ne: true
    },
    name: {
      $regex: `.*${trim(keyword ?? '')}.*`,
      $options: 'i'
    },
    $or: [{ archivedAt: { $exists: false } }, { archivedAt: { $eq: null } }]
  }

  if (isArchived) {
    delete match['$or']
    match.archivedAt = { $exists: true, $ne: null }
  }

  if (isPublished === null) {
    delete match.isPublished
  }

  if (collectionIds?.length > 0) {
    filterCollectionPipeline.push({
      $lookup: {
        from: 'CollectionsProcess',
        localField: '_id',
        foreignField: 'processId',
        as: 'collectionProcesses'
      }
    })
    match['collectionProcesses.collectionId'] = {
      $in: collectionIds
    }
  }

  if (creatorIds?.length > 0) {
    match.userId = {
      $in: creatorIds
    }
  }

  if (tagIds?.length > 0) {
    match['processesTag.tagId'] = {
      $in: tagIds
    }
  }

  if (documentTypeIds?.length > 0) {
    match.documentTypeId = {
      $in: documentTypeIds
    }
  }
  const isBasicUser = userRoleId === AuthRoleIdEnum.BASIC_USER
  if (!isPublished && isBasicUser) {
    match['$or'] = [
      {
        'collaborators.userId': userId
      },
      {
        currentMember: {
          $elemMatch: {
            permissionId: IGroupMemberPermissionId.EDITOR
          }
        },
        $or: [{ archivedAt: { $exists: false } }, { archivedAt: { $eq: null } }]
      },
      {
        userId: userId
      }
    ]
  }

  const groupProcessOnlyPipeline = isBasicUser && userId && isPublished ? getProcessesInGroupOnly(userId) : []

  const processPipeline: AggregationPipeline = [
    ...groupProcessOnlyPipeline,
    ...filterTagPipeline,
    ...filterCollectionPipeline, // TODO check again with basic user
    {
      $match: match
    },
    ...getGroupFilterPipeline(groupIds)
  ]

  processPipeline.push({
    $addFields: {
      currentMemberPermissions: {
        $filter: {
          input: { $concatArrays: ['$groupMembers', '$collectionGroupMembers'] },
          as: 'member',
          cond: { $eq: ['$$member.memberId', userId] }
        }
      }
    }
  })

  if (onlyGetProcessThatUserHaveEditPermission) {
    processPipeline.push({
      $match: {
        currentMemberPermissions: {
          $elemMatch: {
            permissionId: IGroupMemberPermissionId.EDITOR
          }
        }
      }
    })
  }

  const draftPipeline: AggregationPipeline = [
    {
      $lookup: {
        from: 'Collaboration',
        localField: '_id',
        foreignField: 'processId',
        as: 'collaborators'
      }
    },
    {
      $unwind: {
        path: '$collaborators',
        preserveNullAndEmptyArrays: true
      }
    },
    {
      $lookup: {
        from: 'GroupsProcess',
        localField: '_id',
        foreignField: 'processId',
        as: 'groups'
      }
    },
    {
      $lookup: {
        from: 'GroupMember',
        localField: 'groups.groupId',
        foreignField: 'groupId',
        as: 'groupMembers'
      }
    },
    {
      $addFields: {
        currentMember: {
          $filter: {
            input: '$groupMembers',
            as: 'member',
            cond: {
              $eq: ['$$member.memberId', userId]
            }
          }
        }
      }
    },
    ...filterTagPipeline,
    {
      $match: match
    },
    {
      $group: {
        _id: '$_id',
        name: {
          $first: '$name'
        },
        description: {
          $first: '$description'
        },
        owner: {
          $first: '$owner'
        },
        startDate: {
          $first: '$startDate'
        },
        endDate: {
          $first: '$endDate'
        },
        userId: {
          $first: '$userId'
        },
        image: {
          $first: '$image'
        },
        procedureIcon: {
          $first: '$procedureIcon'
        },
        totalTime: {
          $first: '$totalTime'
        },
        organizationId: {
          $first: '$organizationId'
        },
        collectionId: {
          $first: '$collectionId'
        },
        releaseNote: {
          $first: '$releaseNote'
        },
        editorNote: {
          $first: '$editorNote'
        },
        public: {
          $first: '$public'
        },
        status: {
          $first: '$status'
        },
        reason: {
          $first: '$reason'
        },
        primaryGroupId: {
          $first: '$primaryGroupId'
        },
        processWorkflowId: {
          $first: '$processWorkflowId'
        },
        processWorkflowStepId: {
          $first: '$processWorkflowStepId'
        },
        documentTypeId: {
          $first: '$documentTypeId'
        },
        checkedOut: {
          $first: '$checkedOut'
        },
        checkedOutById: {
          $first: '$checkedOutById'
        },
        checkedOutDate: {
          $first: '$checkedOutDate'
        },
        dateSubmitted: {
          $first: '$dateSubmitted'
        },
        reviewerId: {
          $first: '$reviewerId'
        },
        reviewStarted: {
          $first: '$reviewStarted'
        },
        reviewComplete: {
          $first: '$reviewComplete'
        },
        previousStatus: {
          $first: '$previousStatus'
        },
        publishedDate: {
          $first: '$publishedDate'
        },
        rejectedDate: {
          $first: '$rejectedDate'
        },
        rejectedById: {
          $first: '$rejectedById'
        },
        publishedById: {
          $first: '$publishedById'
        },
        updatedAt: {
          $first: '$updatedAt'
        },
        createdAt: {
          $first: '$createdAt'
        },
        createdBy: {
          $first: '$createdBy'
        },
        archivedAt: {
          $first: '$archivedAt'
        },
        steps: {
          $first: '$steps'
        },
        collections: {
          $first: '$collections'
        },
        version: {
          $first: '$version'
        },
        isPublished: {
          $first: '$isPublished'
        },
        collaborators: {
          $addToSet: '$collaborators'
        }
      }
    },
    ...getGroupFilterPipeline(groupIds)
  ]

  if (!isCounting) {
    const dataPipeline: AggregationPipeline = [
      {
        $sort: sortField === 'name' ? { name: 1 } : { [sortField]: -1 }
      },
      {
        $facet: {
          paginatedResults: [
            {
              $skip: skip
            },
            {
              $limit: limit
            },
            {
              $lookup: {
                from: 'Step',
                let: {
                  processId: '$_id'
                },
                pipeline: [
                  {
                    $match: {
                      $expr: {
                        $and: [
                          {
                            $eq: ['$processId', '$$processId']
                          }
                        ]
                      }
                    }
                  },
                  {
                    $lookup: {
                      from: 'Media',
                      localField: '_id',
                      foreignField: 'stepId',
                      as: 'media'
                    }
                  }
                ],
                as: 'steps'
              }
            },
            {
              $lookup: {
                from: 'GroupsProcess',
                localField: '_id',
                foreignField: 'processId',
                as: 'groups'
              }
            },
            {
              $lookup: {
                from: 'Group',
                localField: 'groups.groupId',
                foreignField: '_id',
                as: 'groups'
              }
            },
            {
              $lookup: {
                from: 'DocumentType',
                let: {
                  documentTypeId: '$documentTypeId'
                },
                pipeline: [
                  {
                    $match: {
                      $expr: {
                        $and: [
                          {
                            $eq: ['$_id', '$$documentTypeId']
                          }
                        ]
                      }
                    }
                  },
                  {
                    $lookup: {
                      from: 'Icon',
                      localField: 'iconId',
                      foreignField: '_id',
                      as: 'iconBuilder'
                    }
                  },
                  {
                    $unwind: {
                      path: '$iconBuilder',
                      preserveNullAndEmptyArrays: true
                    }
                  }
                ],
                as: 'documentType'
              }
            },
            {
              $unwind: {
                path: '$documentType',
                preserveNullAndEmptyArrays: true
              }
            },
            {
              $addFields: {
                iconBuilder: '$documentType.iconBuilder'
              }
            },
            {
              $lookup: {
                from: 'Tag',
                localField: 'processesTags.tagId',
                foreignField: '_id',
                as: 'tags'
              }
            },
            {
              $lookup: {
                from: 'User',
                localField: 'createdBy',
                foreignField: '_id',
                as: 'creator'
              }
            },
            {
              $lookup: {
                from: 'User',
                localField: 'userId',
                foreignField: '_id',
                as: 'user'
              }
            },
            {
              $unwind: {
                path: '$creator',
                preserveNullAndEmptyArrays: true
              }
            },
            {
              $unwind: {
                path: '$user',
                preserveNullAndEmptyArrays: true
              }
            },
            {
              $addFields: {
                creatorName: {
                  $concat: ['$creator.firstName', ' ', '$creator.lastName']
                },
                creatorImage: '$creator.image'
              }
            },
            {
              $addFields: {
                creatorName: {
                  $cond: {
                    if: {
                      $or: [
                        { $eq: ['$creatorName', null] },
                        { $eq: ['$creatorName', ''] },
                        { $eq: ['$creatorName', undefined] },
                        { $eq: [{ $type: '$creatorName' }, 'missing'] }
                      ]
                    },
                    then: {
                      $ifNull: [
                        { $concat: ['$user.firstName', ' ', '$user.lastName'] },
                        'Unknown User'
                      ]
                    },
                    else: '$creatorName'
                  }
                },
                creatorImage: {
                  $cond: {
                    if: {
                      $or: [
                        { $eq: ['$creatorImage', null] },
                        { $eq: ['$creatorImage', ''] },
                        { $eq: ['$creatorImage', undefined] },
                        { $eq: [{ $type: '$creatorImage' }, 'missing'] }
                      ]
                    },
                    then: '$user.image',
                    else: '$creatorImage'
                  }
                }
              }
            },
            {
              $project: {
                _id: false,
                id: '$_id',
                name: true,
                description: true,
                owner: true,
                startDate: true,
                endDate: true,
                userId: true,
                image: true,
                procedureIcon: true,
                totalTime: true,
                organizationId: true,
                releaseNote: true,
                editorNote: true,
                public: true,
                status: true,
                reason: true,
                primaryGroupId: true,
                processWorkflowId: true,
                processWorkflowStepId: true,
                documentTypeId: true,
                documentType: true,
                checkedOut: true,
                checkedOutById: true,
                checkedOutDate: true,
                dateSubmitted: true,
                reviewerId: true,
                reviewStarted: true,
                reviewComplete: true,
                previousStatus: true,
                publishedDate: true,
                rejectedDate: true,
                rejectedById: true,
                publishedById: true,
                updatedAt: true,
                createdAt: true,
                steps: true,
                collections: true,
                version: true,
                isPublished: true,
                groups: true,
                archivedAt: true,
                currentMemberPermissions: true,
                iconBuilder: true,
                tags: true,
                creatorName: true,
                creatorImage: true,
                createdBy: true
              }
            }
          ],
          totalResults: [
            {
              $count: 'count'
            }
          ]
        }
      }
    ]

    processPipeline.push(...dataPipeline)
    draftPipeline.push(...dataPipeline)
  }
  return isPublished ? processPipeline : draftPipeline
}

function getProcessesInGroupOnly(userId: number): AggregationPipeline {
  const pipeline = [
    {
      $lookup: {
        from: 'GroupsProcess',
        localField: '_id',
        foreignField: 'processId',
        as: 'groupsProcesses'
      }
    },
    {
      $lookup: {
        from: 'GroupMember',
        localField: 'groupsProcesses.groupId',
        foreignField: 'groupId',
        as: 'groupMembers'
      }
    },
    {
      $lookup: {
        from: 'CollectionsProcess',
        localField: '_id',
        foreignField: 'processId',
        as: 'collectionsProcesses'
      }
    },
    {
      $lookup: {
        from: 'Collection',
        localField: 'collectionsProcesses.collectionId',
        foreignField: '_id',
        as: 'collections'
      }
    },
    {
      $lookup: {
        from: 'CollectionGroup',
        localField: 'collections._id',
        foreignField: 'collectionId',
        as: 'collectionGroups'
      }
    },
    {
      $lookup: {
        from: 'GroupMember',
        localField: 'collectionGroups.groupId',
        foreignField: 'groupId',
        as: 'collectionGroupMembers'
      }
    },
    {
      $addFields: {
        memberIds: {
          $concatArrays: ['$groupMembers.memberId', '$collectionGroupMembers.memberId']
        }
      }
    },
    {
      $match: {
        memberIds: userId
      }
    },
    {
      $lookup: {
        from: 'CollectionsProcess',
        localField: '_id',
        foreignField: 'processId',
        as: 'collections'
      }
    },
    {
      $lookup: {
        from: 'Collection',
        localField: 'collections.collectionId',
        foreignField: '_id',
        as: 'collections',
        pipeline: [{ $project: { name: 1, _id: 0 } }]
      }
    }
  ]

  return pipeline
}
